import { useState } from 'react';
import { Form } from '@design-system';
import { QuickPlanPersonFields } from '@/features/client-quick-plan';
import { ChoiceList } from '../components/choice-list';
import { StepFrame } from '../components/step-frame';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { useConsumerPlan } from '../hooks/use-consumer-plan';
import { toConsumerPartner, usePartnerForm } from '../hooks/use-partner-form';
import type { ConsumerStepProps } from './step-props';

export function HouseholdStep({ clientId }: ConsumerStepProps) {
  const { goTo, back, finish, returnTo } = useConsumerFlow(clientId);
  const { plan, updatePlan } = useConsumerPlan(clientId);
  const [household, setHousehold] = useState<ConsumerHousehold>(plan?.household ?? 'solo');
  const [error, setError] = useState<string | null>(null);
  const partnerForm = usePartnerForm(plan?.partner ?? null);

  const saveAndContinue = (partner: ConsumerPartner | null) => {
    updatePlan({ household, partner });
    if (returnTo) finish('review');
    else goTo('scenario');
  };

  const handleContinue = () => {
    if (household === 'solo') {
      saveAndContinue(null);
      return;
    }
    void partnerForm.handleSubmit(
      values => saveAndContinue(toConsumerPartner(values)),
      () => setError('Complete your partner’s details to continue.'),
    )();
  };

  return (
    <StepFrame
      step="household"
      title="Who would you like coverage for?"
      subtitle="Your current estimate is for you."
      onBack={() => back('setting')}
      help={
        <>
          <p>Some policies offer couples discounts or shared benefits. Neither is automatic, and each person’s eligibility needs review.</p>
          <p>You can keep exploring your own coverage while your partner’s intake is pending.</p>
        </>
      }
      primary={{
        label: household === 'partner' ? 'Continue with my quotes' : 'Continue',
        onClick: handleContinue,
      }}
      error={error}
      next={returnTo ? 'return to your plan.' : 'confirm your care scenario.'}
    >
      <ChoiceList
        label="Who to cover"
        value={household}
        onChange={value => {
          setHousehold(value);
          setError(null);
        }}
        choices={[
          { value: 'solo', title: 'Just me', description: 'Continue with your completed intake.' },
          { value: 'partner', title: 'Me and my partner', description: 'Check whether coverage for two could help.' },
        ]}
      />
      {household === 'partner' && (
        <Form {...partnerForm}>
          <form autoComplete="off" className="flex flex-col gap-4" onSubmit={event => event.preventDefault()}>
            <h2 className="text-base font-semibold">Your partner’s details</h2>
            <QuickPlanPersonFields control={partnerForm.control} fieldPrefix="partner." />
            <p className="text-muted-foreground text-sm">
              Prices are not guaranteed and subject to change with carrier underwriting, qualification, and approval.
            </p>
          </form>
        </Form>
      )}
    </StepFrame>
  );
}
